import React, { lazy, Suspense } from 'react';
import { Loader } from '@roots-ui/loader';

// New
import { ProtectedComponent, FallBackPage, LandingPage, SharedRbac } from '@dara-core/shared';

const LoanSearchContainer = lazy(() =>
  import('@dara-core/loan').then((m) => ({ default: m.LoanSearchContainer }))
);
const LoanContainer = lazy(() =>
  import('@dara-core/loan').then((m) => ({ default: m.LoanContainer }))
);
const LoanDetailsContainer = lazy(() =>
  import('@dara-core/loan-details').then((m) => ({ default: m.LoanDetailsContainer }))
);
const PropertyDetails = lazy(() =>
  import('@dara-core/loan-property').then((m) => ({ default: m.PropertyDetails }))
);
const LoanEscrowContainer = lazy(() =>
  // @ts-ignore
  import('@dara-core/escrow').then((m) => ({ default: m.LoanEscrowContainer }))
);
const LoanDelinquencyContainer = lazy(() =>
  import('@dara-core/loan-delinquency').then((m) => ({
    default: m.LoanDelinquencyContainer,
  }))
);
const LoanDefaultContainer = lazy(() =>
  import('@dara-core/loan-default').then((m) => ({ default: m.LoanDefaultContainer }))
);
const LoanHistoryContainer = lazy(() =>
  import('@dara-core/loan-history').then((m) => ({ default: m.LoanHistoryContainer }))
);
const LoanCashContainer = lazy(() =>
  import('@dara-core/loan-cash').then((m) => ({ default: m.LoanCashContainer }))
);
const AdminContainer = lazy(() =>
  import('@dara-core/admin').then((m) => ({ default: m.AdminContainer }))
);
const BoardingContainer = lazy(() =>
  import('@dara-core/boarding').then((m) => ({ default: m.BoardingContainer }))
);
const InvestorContainer = lazy(() =>
  import('@dara-core/investors').then((m) => ({ default: m.InvestorManagementContainer }))
);
const Profile = lazy(() => 
  import('@dara-core/admin').then((m) => ({ default: m.Profile })));
const WorkflowContainer = lazy(() =>
  import('@dara-core/work-management').then((m) => ({ default: m.WorkflowContainer }))
);
const EscrowContainer = lazy(() =>
  // @ts-ignore
  import('@dara-core/escrow').then((m) => ({ default: m.LoanEscrowContainer }))
);

const CashContainer = lazy(() =>
  import('@dara-core/cash').then((m) => ({ default: m.CashContainer }))
);

// const AIDocsContainer = lazy(() => import('@sagent/docs.docs-container').then((m) => ({ default: m.DocsContainer })));

const AnalyticsContainer = lazy(() =>
  import('@dara-core/analytics').then((m) => ({ default: m.AnalyticsContainer }))
);

function DaraRouteSuspense({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<Loader />}>{children}</Suspense>;
}

const loanRoutes = [
  {
    index: true,
    element: (
      <DaraRouteSuspense>
        <SharedRbac.RBACWrapper requiredSiteMapLevel='section'>
          <ProtectedComponent component={LoanSearchContainer} />
        </SharedRbac.RBACWrapper>
      </DaraRouteSuspense>
    ),
  },
  {
    path: ':loanNumber/*',
    element: (
      <DaraRouteSuspense>
        <SharedRbac.RBACWrapper requiredSiteMapLevel='section'>
          <ProtectedComponent component={LoanContainer} />
        </SharedRbac.RBACWrapper>
      </DaraRouteSuspense>
    ),
    children: [
      { path: '*', element: <FallBackPage /> },
      {
        path: 'default/*',
        element: (
          <DaraRouteSuspense>
            <SharedRbac.RBACWrapper requiredSiteMapLevel='subSection'>
              <ProtectedComponent component={LoanDefaultContainer} />
            </SharedRbac.RBACWrapper>
          </DaraRouteSuspense>
        ),
      },
      {
        path: 'delinquency/*',
        element: (
          <DaraRouteSuspense>
            <SharedRbac.RBACWrapper requiredSiteMapLevel='subSection'>
              <ProtectedComponent component={LoanDelinquencyContainer} />
            </SharedRbac.RBACWrapper>
          </DaraRouteSuspense>
        ),
      },
      {
        path: 'history/*',
        element: (
          <DaraRouteSuspense>
            <SharedRbac.RBACWrapper requiredSiteMapLevel='subSection'>
              <ProtectedComponent component={LoanHistoryContainer} />
            </SharedRbac.RBACWrapper>
          </DaraRouteSuspense>
        ),
      },
      {
        path: 'loan-details/*',
        element: (
          <DaraRouteSuspense>
            <SharedRbac.RBACWrapper requiredSiteMapLevel='subSection'>
              <ProtectedComponent component={LoanDetailsContainer} />
            </SharedRbac.RBACWrapper>
          </DaraRouteSuspense>
        ),
      },
      {
        path: 'loan-escrow/*',
        element: (
          <DaraRouteSuspense>
            <SharedRbac.RBACWrapper requiredSiteMapLevel='subSection'>
              <ProtectedComponent component={LoanEscrowContainer} />
            </SharedRbac.RBACWrapper>
          </DaraRouteSuspense>
        ),
      },
      {
        path: 'money/*',
        element: (
          <DaraRouteSuspense>
            <SharedRbac.RBACWrapper requiredSiteMapLevel='subSection'>
              <ProtectedComponent component={LoanCashContainer} />
            </SharedRbac.RBACWrapper>
          </DaraRouteSuspense>
        ),
      },
      {
        path: 'property/*',
        element: (
          <DaraRouteSuspense>
            <SharedRbac.RBACWrapper requiredSiteMapLevel='subSection'>
              <ProtectedComponent component={PropertyDetails} />
            </SharedRbac.RBACWrapper>
          </DaraRouteSuspense>
        ),
      },
    ],
  },
];

const adminRoutes = [
  {
    path: '*',
    element: (
      <DaraRouteSuspense>
        <SharedRbac.RBACWrapper requiredSiteMapLevel='section'>
          <ProtectedComponent component={AdminContainer} />
        </SharedRbac.RBACWrapper>
      </DaraRouteSuspense>
    ),
  },
];

const transfersRoutes = [
  {
    path: '*',
    element: (
      <DaraRouteSuspense>
        <SharedRbac.RBACWrapper requiredSiteMapLevel='section'>
          <ProtectedComponent component={BoardingContainer} />
        </SharedRbac.RBACWrapper>
      </DaraRouteSuspense>
    ),
  },
];

const investorRoutes = [
  {
    path: '*',
    element: (
      <DaraRouteSuspense>
        <SharedRbac.RBACWrapper requiredSiteMapLevel='section'>
          <ProtectedComponent component={InvestorContainer} />
        </SharedRbac.RBACWrapper>
      </DaraRouteSuspense>
    ),
  },
];

const workflowRoutes = [
  {
    path: '*',
    element: (
      <DaraRouteSuspense>
        <SharedRbac.RBACWrapper requiredSiteMapLevel='section'>
          <ProtectedComponent component={WorkflowContainer} />
        </SharedRbac.RBACWrapper>
      </DaraRouteSuspense>
    ),
  },
];

const ProfileRoutes = [
  {
    path: '*',
    element: (
      <DaraRouteSuspense>
        <ProtectedComponent component={Profile} />
      </DaraRouteSuspense>
    ),
  },
];

const EscrowRoutes = [
  {
    path: '*',
    element: (
      <DaraRouteSuspense>
        <SharedRbac.RBACWrapper requiredSiteMapLevel='section'>
          <ProtectedComponent component={EscrowContainer} />
        </SharedRbac.RBACWrapper>
      </DaraRouteSuspense>
    ),
  },
];

const AnalyticsRoutes = [
  {
    path: '*',
    element: (
      <DaraRouteSuspense>
        <SharedRbac.RBACWrapper requiredSiteMapLevel='section'>
          <ProtectedComponent component={AnalyticsContainer} />
        </SharedRbac.RBACWrapper>
      </DaraRouteSuspense>
    ),
  },
];

const CashRoutes = [
  {
    path: '*',
    element: (
      <DaraRouteSuspense>
        <SharedRbac.RBACWrapper requiredSiteMapLevel='section'>
          <ProtectedComponent component={CashContainer} />
        </SharedRbac.RBACWrapper>
      </DaraRouteSuspense>
    ),
  },
];

// const AIDocsRoutes = [
//   {
//     path: '*',
//     element: (
//       <DaraRouteSuspense>
//         <SharedRbac.RBACWrapper requiredSiteMapLevel='section'>
//           <ProtectedComponent component={AIDocsContainer} />
//         </SharedRbac.RBACWrapper>
//       </DaraRouteSuspense>
//     ),
//   },
// ];

const homeRoutes = [
  {
    index: true,
    element: (
      <DaraRouteSuspense>
        <ProtectedComponent component={LandingPage} />
      </DaraRouteSuspense>
    ),
  },
];

export const daraRoutes = [
  {
    path: '/loans/*',
    children: loanRoutes,
  },
  {
    path: '/home',
    children: homeRoutes,
  },
  {
    path: '/admin/*',
    children: adminRoutes,
  },
  {
    path: '/transfers/*',
    children: transfersRoutes,
  },
  {
    path: '/investor/*',
    children: investorRoutes,
  },
  {
    path: '/my-work/*',
    children: workflowRoutes,
  },
  {
    path: '/escrow/*',
    children: EscrowRoutes,
  },
  {
    path: '/analytics/*',
    children: AnalyticsRoutes,
  },
  {
    path: '/profile/*',
    children: ProfileRoutes,
  },
  {
    path: '/cash/*',
    children: CashRoutes,
  },
  //   {
  //   path: '/docs/*',
  //   children: AIDocsRoutes,
  // },
  { path: '*', element: <FallBackPage /> },
];
